import React, { useState, useEffect } from 'react';
import { FaUserFriends, FaSave, FaTrash } from 'react-icons/fa';
import * as clientService from '../../services/client.service';
import * as wrtBuyerService from '../../services/wrtBuyer.service';

const WRTBuyer = () => {
  const [clients, setClients] = useState([]);
  const [buyerName, setBuyerName] = useState('');
  const [selectedClientIds, setSelectedClientIds] = useState([]);
  const [wrtList, setWrtList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    const loadData = async () => {
      try {
        setLoading(true);
        setError(null);
        const clientList = await clientService.getClientList();
        const wrtBuyers = await wrtBuyerService.getWRTBuyers();
        setClients(Array.isArray(clientList) ? clientList : []);
        setWrtList(Array.isArray(wrtBuyers) ? wrtBuyers : []);
      } catch (err) {
        setError('Failed to load data. Please try again.');
        setClients([]);
        setWrtList([]);
      } finally {
        setLoading(false);
      }
    };
    loadData();
  }, []);

  const handleToggleClient = (clientId) => {
    setSelectedClientIds((prev) =>
      prev.includes(clientId) ? prev.filter((id) => id !== clientId) : [...prev, clientId]
    );
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!buyerName.trim() || selectedClientIds.length === 0) return;
    
    try {
      setSubmitting(true);
      setError(null);
      const payload = {
        buyerName: buyerName.trim(),
        clientIds: selectedClientIds,
      };
      console.log(payload);
      const newBuyer = await wrtBuyerService.createWRTBuyer(payload);
      setWrtList([newBuyer, ...wrtList]);
      setBuyerName('');
      setSelectedClientIds([]);
    } catch (err) {
      setError('Failed to save WRT Buyer. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (id) => {
    try {
      setError(null);
      await wrtBuyerService.deleteWRTBuyer(id);
      setWrtList(wrtList.filter((w) => w.id !== id));
    } catch (err) {
      setError('Failed to delete WRT Buyer. Please try again.');
    }
  };

  const getClientLabel = (c) => `${c.name || 'Unknown'} (${c.clientId || '-'})`;

  return (
    <div style={{ background: 'linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%)', minHeight: '100vh', paddingBottom: '3rem' }}>
      <div className="container-lg mt-2">
        <div className="mb-4">
          <div className="d-flex align-items-center gap-3">
            <div style={{ background: 'linear-gradient(135deg, #8e44ad 0%, #3498db 100%)', borderRadius: '12px', padding: '12px', color: '#fff' }}>
              <FaUserFriends size={28} />
            </div>
            <div>
              <h1 className="mb-0" style={{ fontSize: '2rem', fontWeight: '700', color: '#2c3e50' }}>WRT Buyer</h1>
              <p className="mb-0 text-muted">Manage buyers and their associated clients</p>
            </div>
          </div>
        </div>

        <div className="card border-0 shadow-sm mb-4" style={{ borderTop: '4px solid #8e44ad' }}>
          <div className="card-header bg-white border-0 pb-0">
            <h5 className="mb-0" style={{ color: '#8e44ad', fontWeight: '600' }}>Add WRT Buyer</h5>
          </div>
          <div className="card-body">
            {error && (
              <div className="alert alert-danger alert-dismissible fade show" role="alert">
                {error}
                <button type="button" className="btn-close" onClick={() => setError(null)}></button>
              </div>
            )}
            {loading ? (
              <div className="text-center py-4">
                <div className="spinner-border" role="status">
                  <span className="visually-hidden">Loading...</span>
                </div>
              </div>
            ) : (
              <form onSubmit={handleSubmit}>
                <div className="row">
                  <div className="col-lg-12 mb-3">
                    <label className="form-label fw-600" style={{ color: '#2c3e50' }}>Buyer Name <span className="text-danger">*</span></label>
                    <input
                      type="text"
                      className="form-control form-control-lg"
                      value={buyerName}
                      onChange={(e) => setBuyerName(e.target.value)}
                      placeholder="Enter buyer name"
                      required
                      style={{ borderRadius: '8px', borderColor: '#e0e0e0' }}
                    />
                  </div>
                  <div className="col-lg-12 mb-3">
                    <label className="form-label fw-600" style={{ color: '#2c3e50' }}>Client List <span className="text-danger">*</span></label>
                    <p className="text-muted small mb-2">Select one or more clients (from CRM Master → Add Client)</p>
                    <div className="border rounded p-3" style={{ maxHeight: '240px', overflowY: 'auto', background: '#f8f9fa' }}>
                      {clients.length === 0 ? (
                        <p className="text-muted mb-0">No clients found. Add clients from CRM Master → Add Client first.</p>
                      ) : (
                        <div className="d-flex flex-column gap-2">
                          {clients.map((c) => {
                            const id = c.id;
                            const checked = selectedClientIds.includes(id);
                            return (
                              <label key={id} className="d-flex align-items-center gap-2 cursor-pointer mb-0">
                                <input
                                  type="checkbox"
                                  checked={checked}
                                  onChange={() => handleToggleClient(id)}
                                />
                                <span>{getClientLabel(c)}</span>
                              </label>
                            );
                          })}
                        </div>
                      )}
                    </div>
                  </div>
                </div>
                <button type="submit" className="btn btn-primary" style={{ borderRadius: '8px', fontWeight: '600' }} disabled={!buyerName.trim() || selectedClientIds.length === 0 || submitting}>
                  <FaSave className="me-2" /> {submitting ? 'Saving...' : 'Save WRT Buyer'}
                </button>
              </form>
            )}
          </div>
        </div>

        {wrtList.length > 0 && (
          <div className="card border-0 shadow-sm" style={{ borderTop: '4px solid #3498db' }}>
            <div className="card-header bg-white border-0 pb-0">
              <h5 className="mb-0" style={{ color: '#3498db', fontWeight: '600' }}>WRT Buyer List</h5>
            </div>
            <div className="card-body">
              <div className="table-responsive">
                <table className="table table-hover table-bordered align-middle">
                  <thead style={{ background: '#f8f9fa' }}>
                    <tr>
                      <th style={{ fontWeight: '600' }}>Buyer Name</th>
                      <th style={{ fontWeight: '600' }}>Clients</th>
                      <th style={{ fontWeight: '600', width: '100px' }}>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {wrtList.map((w) => (
                      <tr key={w.id}>
                        <td>{w.buyerName}</td>
                        <td>
                          <div className="d-flex flex-wrap gap-1">
                            {(w.clients || []).map((c, i) => (
                              <span key={i} className="badge bg-secondary">
                                {c.name} ({c.clientId})
                              </span>
                            ))}
                            {(!w.clients || w.clients.length === 0) && <span className="text-muted">-</span>}
                          </div>
                        </td>
                        <td>
                          <button type="button" className="btn btn-sm btn-outline-danger" onClick={() => handleDelete(w.id)}>
                            <FaTrash />
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default WRTBuyer;
